import PropTypes from 'prop-types';
import {useEffect} from 'react';

import Loading from 'components/Loading';
import useSearchBar from 'components/Search/Bar/Provider';
import SideNav from 'components/SideNav';
import {getMenuItems, getTabComponent} from 'components/SideNav/utils';
import ErrorMessage from 'components/errors/ErrorMessage';
import KyError from 'components/errors/KyError';
import NotFound from 'components/shared/error/not_found';
import FullWidthResourceTemplate from 'components/templates/FullWidthResource';
import useAppContext from 'conf/AppContext';
import Types from 'conf/types';
import usePediatricContext from 'context/Pediatric';
import {useGet} from 'helpers/KyHooks';
import VariantHaplotypesTab from 'pages/Variant/HaplotypesTab';
import VariantOverviewTab from 'pages/Variant/OverviewTab';

const propTypes = {
  id: PropTypes.string.isRequired,
  section: PropTypes.string,
  loadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
/**
 * Renders variant page.
 */
export default function VariantPage({id, section = 'overview', loadId = 0}) {
  const appContext = useAppContext();
  const pediatricContext = usePediatricContext();
  const searchBar = useSearchBar();
  const showOverview = !section || section === 'overview';

  const {response, error} = useGet(`site/variant/${id}`, {
    cacheResponse: !appContext.isPreview,
  }, {
    loadId,
  });
  useEffect(() => {
    if (response) {
      searchBar.initializeSearchBar([{id, objCls: 'variant', name: response.data.variant.name}]);
    }
  }, [response]);

  let name = `Variant ${id}`;
  let body = <Loading />;
  let counts = {};
  if (error) {
    if (error.response?.status === 404) {
      return <NotFound />;
    }
    body = <KyError kyError={error} />;
  } else if (response) {
    counts = response.data.counts;
    const {variant, needsApproval, description, frequencySources, vips, alleles} = response.data;
    name = variant.symbol || variant.name;
    if (showOverview) {
      body = (
        <VariantOverviewTab
          variant={variant}
          counts={counts}
          needsApproval={needsApproval}
          description={description}
          frequencySources={frequencySources}
          vips={vips}
          alleles={alleles}
        />
      );
    }
  }
  const Comp = getTabComponent(section, pediatricContext.isPediatricMode);
  if (Comp) {
    body = <Comp id={id} objCls="variant" name={name} />;
  } else if (section === 'haplotype') {
    body = <VariantHaplotypesTab id={id} />;
  } else if (!showOverview) {
    body = <ErrorMessage message={`Unsupported tab "${section}"`} />;
  }


  const baseUrl = Types.variant.url(id);
  const menuItems = getMenuItems('variant', baseUrl, counts);

  return (
    <FullWidthResourceTemplate id={id} type="variant" name={name}>
      <SideNav items={menuItems}>
        {body}
      </SideNav>
    </FullWidthResourceTemplate>
  );
}
VariantPage.propTypes = propTypes;
